import React, { useState } from 'react';
import type { AppAnnouncement, Page } from '../types';
import PlusIcon from '../components/icons/PlusIcon';
import TrashIcon from '../components/icons/TrashIcon';

interface CustomerAppAnnouncementsPageProps {
  announcements: AppAnnouncement[];
  onSave: (row: AppAnnouncement) => void | Promise<void>;
  onDelete: (id: string) => void | Promise<void>;
  onNavigate?: (page: Page) => void;
}

const CustomerAppAnnouncementsPage: React.FC<CustomerAppAnnouncementsPageProps> = ({
  announcements,
  onSave,
  onDelete,
  onNavigate,
}) => {
  const [draft, setDraft] = useState<AppAnnouncement | null>(null);

  return (
    <div className="flex flex-col h-full">
      <header className="bg-white shadow-md px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">App announcements</h1>
          <p className="text-sm text-gray-500">
            Shows on the customer app Home under <strong>Coming up</strong> (synced
            <code className="mx-1 text-xs">app_announcements</code> → app). Use for future
            specials, events, or menu launches before they are live offers.
          </p>
        </div>
        <div className="flex gap-2">
          {onNavigate && (
            <button type="button" className="text-sm text-pink-600" onClick={() => onNavigate('loyalty')}>
              ← Loyalty &amp; offers
            </button>
          )}
          <button
            type="button"
            className="bg-pink-500 text-white font-bold py-2 px-4 rounded-lg flex items-center"
            onClick={() =>
              setDraft({
                id: '',
                title: '',
                description: '',
                isActive: true,
                sortOrder: announcements.length,
              })
            }
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add
          </button>
        </div>
      </header>
      <main className="flex-1 p-6 overflow-y-auto space-y-3">
        {announcements.map((a) => (
          <div key={a.id} className="bg-white border rounded-lg p-4 flex justify-between gap-4">
            <div>
              <p className="font-semibold text-gray-900">{a.title}</p>
              <p className="text-sm text-gray-600">{a.description}</p>
              <p className="text-xs text-gray-400 mt-1">
                sort {a.sortOrder} · {a.isActive ? 'active' : 'inactive'}
              </p>
            </div>
            <div className="flex gap-2">
              <button type="button" className="text-pink-600 text-sm" onClick={() => setDraft({ ...a })}>
                Edit
              </button>
              <button
                type="button"
                className="text-red-600"
                onClick={() => {
                  if (window.confirm('Delete announcement?')) void onDelete(a.id);
                }}
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        ))}
        {announcements.length === 0 && (
          <p className="text-sm text-gray-500">No announcements yet.</p>
        )}
      </main>
      {draft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 space-y-3">
            <h2 className="text-lg font-bold">Announcement</h2>
            <input
              className="w-full border rounded-md px-3 py-2"
              placeholder="Title"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            />
            <textarea
              className="w-full border rounded-md px-3 py-2"
              rows={3}
              placeholder="Description"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
              />
              Active
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="px-4 py-2 border rounded-md" onClick={() => setDraft(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-pink-500 text-white rounded-md"
                onClick={() => {
                  if (!draft.title.trim()) {
                    alert('Title required');
                    return;
                  }
                  void onSave({ ...draft, id: draft.id || `ann-${Date.now()}` });
                  setDraft(null);
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerAppAnnouncementsPage;
